<template>
  <bulk-update-wrapper
    :saving="saving"
    title="Approve Jobs"
    :button-label="buttonLabel"
    :has-changes="true"
    @save="save"
    @dismiss="dismiss"
  >
    <vs-text>
      {{ selectedJobs.length }}
      {{ selectedJobs.length > 1 ? 'jobs' : 'job' }} will be marked as approved
    </vs-text>

    <template #error-snackbar>
      <vs-snackbar :value="error" type="error" sticky @input="clearSnackbar">
        Failed to approve jobs
      </vs-snackbar>
    </template>
  </bulk-update-wrapper>
</template>

<script setup lang="ts">
import BulkUpdateWrapper from '../common/BulkUpdateWrapper.vue'
import { ref, computed } from 'vue'
import { JobJson } from '@/models/types/job-json'
import { BulkUpdateResponse } from '@/models/types/bulk-update-response'
import { useBulkApproveMutation } from '@/api/jobs'

const emits = defineEmits<{
  (e: 'dismiss'): void
  (e: 'updated', response: BulkUpdateResponse): void
}>()

const props = defineProps<{
  selectedJobs: JobJson[]
}>()

const error = ref(false)
const saving = ref(false)

const dismiss = () => emits('dismiss')
const clearSnackbar = () => (error.value = false)

const { mutateAsync } = useBulkApproveMutation()

const buttonLabel = computed<string>(() => {
  const jobCount = props.selectedJobs.length
  return jobCount >= 2 ? `Approve ${jobCount} jobs` : 'Approve job'
})

const save = async () => {
  saving.value = true
  try {
    await mutateAsync({
      jobIds: props.selectedJobs.map((job) => job.id),
    })
    const updatedJobs: JobJson[] = props.selectedJobs.map(({ ...job }) =>
      Object.assign(job, { approval_status: 'Approved' }),
    )
    emits('updated', { message: 'Jobs approved', updatedJobs })
  } catch {
    error.value = true
  } finally {
    saving.value = false
  }
}
</script>
